import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, CHART_TONES, HorizontalBarList, Icon, Skeleton, cn, cssVars, type ChartTone } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { shareWidths, type AnalyticsFilters } from "./analytics-model";
import { ChartCard } from "./chart-card";
import { useFeedback, useLanguages } from "./use-analytics";

const LANGUAGE_TONES: readonly ChartTone[] = ["brand", "brand-light", "ink", "signal", "ink-light"];

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  );
}

/** Language mix: one segmented 100% bar + legend with percentages. */
export function LanguageCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const query = useLanguages(filters);
  const name = (key: string) => t(`engage.analytics.languages.names.${key}`, { defaultValue: key });
  return (
    <ChartCard
      testId="analytics-languages"
      title={t("engage.analytics.languages.title")}
      aside={<span className="text-xs text-fg-subtle">{t("engage.analytics.languages.subtitle")}</span>}
      bodyClassName="space-y-4"
      query={query}
      isEmpty={(data) => data.total === 0}
      empty={{ icon: "languages", body: t("engage.analytics.languages.emptyBody") }}
      skeleton={<ListSkeleton rows={4} />}
    >
      {(data) => {
        const widths = shareWidths(data.items.map((item) => item.pct));
        const tone = (i: number) => CHART_TONES[LANGUAGE_TONES[i % LANGUAGE_TONES.length] ?? "brand"].swatch;
        return (
          <>
            <div aria-hidden="true" className="flex h-3 overflow-hidden rounded-pill bg-sunken">
              {data.items.map((item, i) => (
                <div key={item.key} className={cn("h-3 w-[var(--geo-width)]", tone(i))} style={cssVars({ "--geo-width": `${(widths[i] ?? 0).toString()}%` })} />
              ))}
            </div>
            <ul className="space-y-2 text-sm">
              {data.items.map((item, i) => (
                <li key={item.key} className="flex items-center gap-2">
                  <span aria-hidden="true" className={cn("size-2.5 rounded-xs", tone(i))} />
                  <span>{name(item.key)}</span>
                  <span className="ml-auto font-semibold tabular-nums">{fmt.percent(item.pct)}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-fg-subtle">{t("engage.analytics.languages.note")}</p>
          </>
        );
      }}
    </ChartCard>
  );
}

/** Thumbs-down reasons (count of total) + link to gaps. */
export function FeedbackCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { summary } = useWorkspaceContext();
  const query = useFeedback(filters);
  return (
    <ChartCard
      testId="analytics-feedback"
      title={t("engage.analytics.feedback.title")}
      aside={query.data ? <span className="text-xs text-fg-subtle">{t("engage.analytics.feedback.thumbsDown", { count: query.data.thumbs_down })}</span> : null}
      bodyClassName="space-y-3"
      query={query}
      isEmpty={(data) => data.thumbs_down === 0}
      empty={{ icon: "thumbs-up", title: t("engage.analytics.feedback.emptyTitle"), body: t("engage.analytics.feedback.emptyBody") }}
      skeleton={<ListSkeleton rows={5} />}
    >
      {(data) => (
        <>
          <HorizontalBarList
            aria-label={t("engage.analytics.feedback.title")}
            percent={false}
            tone="ink"
            items={data.reasons.map((reason) => ({
              label: t(`engage.analytics.feedback.reasons.${reason.key}`, { defaultValue: reason.key }),
              value: reason.count,
              display: t("engage.analytics.feedback.ofTotal", { count: fmt.number(reason.count), total: fmt.number(data.thumbs_down) }),
            }))}
          />
          <AppLink href={consoleRoutes.gaps(summary.slug)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary-ink hover:underline">
            {t("engage.analytics.feedback.review")}
            <Icon name="arrow-right" className="size-3" />
          </AppLink>
        </>
      )}
    </ChartCard>
  );
}
